import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { useLocation } from 'react-router-dom';
import { AiChatMessage, Handover, HandoverChatResponse } from '@handover/shared-types';
import ChatGPTWindow from './ChatGPTWindow';
import { useGetAllHandoversByCreatedByLoggedInUserQuery } from '../redux/handover/handoverApi';
import { useSendMessageToHandoverChatMutation } from '../redux/ai/aiApi';
import { useGetMyCompanyQuery } from '../redux/company/companyApi';
import { getCompanyAiState } from '../helpers/companyAiHelper';

interface HandoverChatbotProps {
  onClose: () => void;
}

type ChatMessage = AiChatMessage & {
  citations?: HandoverChatResponse['citations'];
  grounded?: boolean;
};

const SESSION_STORAGE_KEY = 'he:selected-handover-chat';

const parseCurrentHandoverId = (pathname: string): string | null => {
  const match = pathname.match(/^\/handovers\/([^/]+)$/);
  return match?.[1] || null;
};

const getHandoverLabel = (handover: Handover) =>
  handover.task?.trim() || handover.position?.trim() || handover.id;

export const HandoverChatbot = ({ onClose }: HandoverChatbotProps) => {
  const theme = useTheme();
  const location = useLocation();
  const { data: handovers = [], isLoading: handoversLoading } =
    useGetAllHandoversByCreatedByLoggedInUserQuery();
  const { data: companyWorkspace } = useGetMyCompanyQuery();
  const aiState = getCompanyAiState(companyWorkspace?.company);
  const [sendMessageToHandoverChat, { isLoading }] =
    useSendMessageToHandoverChatMutation();

  const [selectedHandoverId, setSelectedHandoverId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const currentRouteHandoverId = parseCurrentHandoverId(location.pathname);
  const selectedHandover =
    handovers.find(handover => handover.id === selectedHandoverId) || null;

  useEffect(() => {
    if (!handovers.length) {
      return;
    }

    const sessionHandoverId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const preferredHandoverId =
      currentRouteHandoverId &&
      handovers.some(handover => handover.id === currentRouteHandoverId)
        ? currentRouteHandoverId
        : sessionHandoverId &&
            handovers.some(handover => handover.id === sessionHandoverId)
          ? sessionHandoverId
          : handovers[0].id;

    setSelectedHandoverId(prev => prev || preferredHandoverId);
    setShowPicker(!preferredHandoverId);
  }, [currentRouteHandoverId, handovers]);

  useEffect(() => {
    if (selectedHandoverId) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, selectedHandoverId);
    }
  }, [selectedHandoverId]);

  const filteredHandovers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return handovers;
    }

    return handovers.filter(handover =>
      [handover.id, handover.task, handover.position, handover.department]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(needle))
    );
  }, [handovers, search]);

  const handleSelectHandover = (handoverId: string) => {
    setSelectedHandoverId(handoverId);
    setShowPicker(false);
    setMessages([]);
    setError(null);
  };

  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || !selectedHandoverId || !aiState.canUseAi) {
      return;
    }

    const nextMessages: ChatMessage[] = [
      ...messages,
      { role: 'user', content: trimmedInput },
    ];

    setMessages(nextMessages);
    setInput('');
    setError(null);

    try {
      const response = await sendMessageToHandoverChat({
        handoverId: selectedHandoverId,
        messages: nextMessages.map(message => ({
          role: message.role,
          content: message.content,
        })),
      }).unwrap();

      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: response.answer,
          citations: response.citations,
          grounded: response.grounded,
        },
      ]);
    } catch (requestError: any) {
      setError(
        requestError?.data?.message ||
          requestError?.message ||
          'Unable to answer from this handover right now.'
      );
    }
  };

  return (
    <ChatGPTWindow
      default={{
        x: window.innerWidth / 2 - 260,
        y: window.innerHeight / 2 - 300,
        width: 560,
        height: 680,
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          className="draggable-handle"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            cursor: 'move',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Ask a Handover
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {selectedHandover ? `Asking about: ${getHandoverLabel(selectedHandover)}` : 'Select a handover first'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              aria-label="change handover"
              onClick={() => setShowPicker(value => !value)}
              sx={{ color: 'inherit' }}
            >
              <SwapHorizOutlinedIcon />
            </IconButton>
            <IconButton aria-label="close" onClick={onClose} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {aiState.alertMessage ? (
          <Alert severity={aiState.alertSeverity || 'info'} sx={{ m: 2, mb: 0 }}>
            {aiState.alertMessage}
          </Alert>
        ) : null}

        {showPicker || !selectedHandover ? (
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <TextField
              fullWidth
              size="small"
              label="Search handovers"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
            <Stack spacing={1} sx={{ mt: 2, maxHeight: 220, overflowY: 'auto' }}>
              {handoversLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading handovers...
                </Typography>
              ) : filteredHandovers.length ? (
                filteredHandovers.map(handover => (
                  <Paper
                    key={handover.id}
                    variant={selectedHandoverId === handover.id ? 'elevation' : 'outlined'}
                    sx={{ p: 1.5, borderRadius: 2 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {getHandoverLabel(handover)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {handover.id}
                          {handover.department ? ` • ${handover.department}` : ''}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSelectHandover(handover.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Select
                      </Button>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No matching handovers found.
                </Typography>
              )}
            </Stack>
          </Box>
        ) : null}

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {!messages.length ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Ask a question about the selected handover. Answers are limited to that handover’s
                details, tasks, and comments.
              </Typography>
            </Box>
          ) : (
            messages.map((message, index) => {
              const isUser = message.role === 'user';
              return (
                <Box
                  key={`${message.role}-${index}`}
                  sx={{
                    alignSelf: isUser ? 'flex-end' : 'flex-start',
                    maxWidth: '88%',
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: isUser ? theme.palette.primary.light : theme.palette.background.default,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isUser
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Paper>
                  {!isUser && message.citations?.length ? (
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      {message.citations.map(citation => (
                        <Chip
                          key={citation.sourceId}
                          size="small"
                          variant="outlined"
                          label={`${citation.label}: ${citation.snippet}`}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Box>
              );
            })
          )}
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Reading the selected handover...
            </Typography>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={
                selectedHandover
                  ? `Ask about ${getHandoverLabel(selectedHandover)}`
                  : 'Select a handover first'
              }
              value={input}
              onChange={event => setInput(event.target.value)}
              disabled={!selectedHandover || !aiState.canUseAi || isLoading}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                void handleSend();
              }}
              disabled={!input.trim() || !selectedHandover || !aiState.canUseAi || isLoading}
              sx={{ minWidth: 96, textTransform: 'none' }}
              startIcon={<SendIcon />}
            >
              Send
            </Button>
          </Stack>
        </Box>
      </Box>
    </ChatGPTWindow>
  );
};

export default HandoverChatbot;
